/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ExchangeAlt from "@monsma/XDL/Icons/ExchangeAlt";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: CompareProductsCheckboxStyles;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => ({
    settingsCollection: getSettingsCollection(state),
    productCompareChecked: getProductCompareChecked(state, props.productContext),
    isProductCompareFull: getIsProductCompareFull(state),
});

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasProductContext;

export interface CompareProductsCheckboxStyles {
    wrapper?: InjectableCss;
    compareCheckbox?: CheckboxPresentationProps;
    icon?: IconProps;
}

export const compareProductsCheckboxStyles: CompareProductsCheckboxStyles = {
    compareCheckbox: {
        typographyProps: {
            lineHeight: "15px",
            weight: 600,
            size: "12px",
            color: "common.ui2",
            css: css`
                &&& {
                    position: relative;
                    margin-left: 0;
                    width: 71px;
                    text-align: right;
                    cursor: pointer;

                    &,
                    > span {
                        transition: all 0.2s linear;
                    }

                    &:hover {
                        color: ${getColor("common.ui3")};
                    }
                }
            `,
        },
        css: css`
            span[role="checkbox"] {
                visibility: hidden;
                position: absolute;
            }
        `,
    },
    icon: {
        src: ExchangeAlt,
        size: 12,
        color: "common.ui5",
        css: css`
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            transition: all 0.2s linear;
        `,
    },
};

const CompareProductsCheckbox: FC<Props> = ({
    productContext,
    isProductCompareFull,
    settingsCollection,
    productCompareChecked,
    extendedStyles,
    addProductToCompare,
    removeProductFromCompare,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(compareProductsCheckboxStyles, extendedStyles));

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };

    if (!settingsCollection.productSettings.enableProductComparisons) {
        return null;
    }

    return (
        <StyledWrapper
            data-test-selector="productListCompareProductsCheckbox"
            data-test-key={productContext.product.id}
        >
            <Checkbox {...styles.compareCheckbox} checked={productCompareChecked} onChange={handleChanged}>
                {translate("Compare")}
                <IconMemo {...styles.icon} />
            </Checkbox>
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(CompareProductsCheckbox));
